import {
  createContext, useCallback, useContext, useMemo, useState, type ReactNode,
} from 'react';
import type { Workspace } from '@/components/layout/navConfig';

/**
 * Which side of the business is on screen — B2C or B2B.
 *
 * <p>
 * A travel company that sells both does it on the same data: the same destinations, the same hotels,
 * the same enquiries. What changes is who the customer is, and therefore what the sidebar, the
 * dashboard and the assistant should be talking about. So this is a VIEW over one workspace, not a
 * second workspace — nothing here scopes a query or hides a record.
 * </p>
 * <p>
 * <strong>Not a security boundary.</strong> Switching to B2B does not grant anything: permissions are
 * checked on the server, and the sidebar filters by them exactly as before. Somebody without
 * Agent.View will not see agent screens in either workspace once those screens exist.
 * </p>
 * <p>
 * Kept in the browser rather than on the tenant, deliberately. It is a preference about what one
 * person is doing this afternoon, not a fact about the company — an operator who works the agent
 * desk in the morning and retail in the afternoon switches twice a day, and storing that on the
 * company would have them changing it for everybody.
 * </p>
 */
interface WorkspaceStoreValue {
  workspace: Workspace;
  setWorkspace: (workspace: Workspace) => void;
  /** True when the B2B side is the one on screen — for a dashboard or header that reads differently. */
  isB2B: boolean;
}

const WorkspaceStoreContext = createContext<WorkspaceStoreValue | undefined>(undefined);

const KEY = 'st.workspace';

/**
 * B2C, unless this browser was last left in B2B.
 *
 * <p>
 * Read defensively. A private window, cleared site data or a value written by an older build all
 * come back as something that is not a workspace, and the honest answer to "I don't know" is the
 * retail side — it is the one that is fully built.
 * </p>
 */
function initial(): Workspace {
  try {
    return localStorage.getItem(KEY) === 'b2b' ? 'b2b' : 'b2c';
  } catch {
    return 'b2c';
  }
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [workspace, setState] = useState<Workspace>(initial);

  const setWorkspace = useCallback((next: Workspace) => {
    setState(next);

    // Best effort, and silent on failure. Losing the preference means opening on B2C tomorrow, which
    // is a small annoyance; throwing here would break the switch itself, which is not.
    try {
      localStorage.setItem(KEY, next);
    } catch { /* storage unavailable — the switch still works for this session */ }
  }, []);

  const value = useMemo<WorkspaceStoreValue>(
    () => ({ workspace, setWorkspace, isB2B: workspace === 'b2b' }),
    [workspace, setWorkspace]);

  return (
    <WorkspaceStoreContext.Provider value={value}>{children}</WorkspaceStoreContext.Provider>
  );
}

export function useWorkspace(): WorkspaceStoreValue {
  const ctx = useContext(WorkspaceStoreContext);
  if (!ctx) throw new Error('useWorkspace must be used inside <WorkspaceProvider>.');
  return ctx;
}
